<template>
	<div>
		<bread-crumbs
			:bread-crumbs="breadCrumbs"
			:current-title="$gettext('New Content Page')"
			class="breadcrumbstyle"
		/>
		<v-card>
			<v-card-text>
				<v-form
					ref="form"
					v-model="pageForm"
				>
					<div v-if="currentPage">
						<v-row align="center">
							<v-col
								cols="12"
								sm="6"
							>
								<v-autocomplete
									v-if="domainPages"
									v-model="currentPage.parentId"
									:label="$gettext('Parent Page')"
									:items="domainPages"
									item-value="id"
									:disabled="currentPage.languageId === null || currentPage.domainId === null"
									:item-title="parentPageText"
									variant="outlined"
								/>
							</v-col>
							<v-col
								cols="12"
								sm="6"
							>
								<v-text-field
									v-model="currentPage.shortname"
									:label="$gettext('Shortname')"
									variant="outlined"
								/>
							</v-col>
						</v-row>
						<v-row align="center">
							<v-col
								cols="12"
								sm="6"
							>
								<v-autocomplete
									v-model="pageTemplate"
									:label="$gettext('Page template')"
									:items="pageTemplates"
									return-object
									:item-title="pageTemplateText"
									variant="outlined"
									@update:model-value="changeTemplate"
								>
									<template #append-inner>
										<v-tooltip
											location="bottom"
										>
											<template #activator="{ props }">
												<v-icon
													color="primary"
													v-bind="props"
													icon="$info"
												/>
											</template>
											<span><translate>Html changes will affect all pages using this page template</translate></span>
										</v-tooltip>
									</template>
								</v-autocomplete>
							</v-col>
							<v-col
								cols="12"
								sm="6"
							>
								<v-text-field
									v-model="currentPage.page.title"
									:rules="[v => !!v || $gettext('Title is required')]"
									:label="$gettext('Title')"
									variant="outlined"
								>
									<template #append-inner>
										<v-tooltip
											location="bottom"
										>
											<template #activator="{ props }">
												<v-icon
													color="primary"
													v-bind="props"
													icon="$info"
												/>
											</template>
											<span><translate>Changing title will change it for all pages using the same page template</translate></span>
										</v-tooltip>
									</template>
								</v-text-field>
							</v-col>
						</v-row>
						<v-row align="center">
							<v-col
								cols="12"
								sm="6"
							>
								<v-select
									v-model="currentPage.languageId"
									:items="languages"
									:label="$gettext('Language')"
									:disabled="currentPage.parentId !== null"
									item-title="name"
									item-value="id"
									variant="outlined"
									@update:model-value="languageDomainUpdate"
								/>
							</v-col>
							<v-col
								cols="12"
								sm="6"
							>
								<v-text-field
									v-model="currentPage.orderingPriorityStrength"
									:label="$gettext('Priority')"
									variant="outlined"
								/>
							</v-col>
						</v-row>
						<v-row align="center">
							<v-col
								cols="12"
								sm="6"
							>
								<v-select
									v-model="currentPage.domainId"
									:items="domains"
									:label="$gettext('Domain')"
									:disabled="currentPage.parentId !== null"
									item-title="name"
									item-value="id"
									variant="outlined"
									@update:model-value="languageDomainUpdate"
								/>
							</v-col>
							<v-col
								cols="12"
								sm="2"
							>
								<v-switch
									v-model="currentPage.published"
									:label="$gettext('Published')"
									color="primary"
								/>
							</v-col>
							<v-col
								cols="12"
								sm="2"
							>
								<v-switch
									v-model="currentPage.private"
									:label="$gettext('Private')"
									color="primary"
								/>
							</v-col>
							<v-col
								cols="12"
								sm="2"
							>
								<v-switch
									v-model="currentPage.downloadable"
									:label="$gettext('Add download button')"
									color="primary"
								/>
							</v-col>
						</v-row>
					</div>
					<hr>
					<h4>HTML</h4>
					<hr>
					<textarea
						id="body"
					/>
				</v-form>
			</v-card-text>
			<v-card-actions>
				<v-spacer />
				<v-btn
					color="primary"
					@click="previewPage"
				>
					<translate>Preview</translate>
				</v-btn>
				<v-btn
					color="primary"
					class="ml-4"
					variant="flat"
					@click="submitPage"
				>
					<translate>
						Save
					</translate>
				</v-btn>
			</v-card-actions>
		</v-card>
	</div>
</template>

<script>
import * as ace from 'ace-builds';
import {
	axios,
} from '../../../../services/axios-service';
import {
	displayError, inform,
} from 'laravel-front-library';
import {
	debounce,
} from 'lodash';

export default {
	name: 'ContentPageCreate',

	data () {
		return {
			breadCrumbs: {
				'/bo/pages': 'Pages'
			},
			pageForm: null,
			currentPage: {
				domainId: null,
				downloadable: false,
				languageId: null,
				orderingPriorityStrength: null,
				pageId: null,
				page: {
					id: null,
					title: null,
					html: '',
				},
				parentId: null,
				private: false,
				published: false,
				shortname: null
			},
			pageTemplates: [],
			pageTemplate: null,
			domainPages: [],
			editor: null,
			languages: [],
			domains: [],
		};
	},

	mounted () {
		this.editor = ace.edit('body');
		this.editor.setTheme('ace/theme/tomorrow');
		this.editor.getSession().setMode('ace/mode/smarty');
		this.getPageData();
	},

	methods: {
		parentPageText: (item) => item.shortname + (item.domain ? (' - ' + item.domain.name + ' - ' + item.language.name) : ''),

		/**
		 * Get selector text for a page template
		 *
		 * @param {object} item
		 * @returns {string}
		 */
		pageTemplateText (item) {
			return (item.id ? item.title : this.$gettext('New page template')) + (item.language ? (' - ' + item.language) : '');
		},

		/**
		 * Get all data for the page
		 *
		 * @returns {void}
		 */
		async getPageData () {
			const noParent = {
				id: null,
				shortname: 'No parent page',
			};
			const newTemplate = {
				id: null,
				title: '',
				html: ''
			};
			const dataError = this.$gettext('Something has gone wrong during data retrieval');

			this.domainPages = [noParent, ];
			this.pageTemplate = newTemplate;

			axios.get('/api/v0/pages/withlanguages').then(({
				data: data
			}) => {
				data.unshift(newTemplate);
				this.pageTemplates = data;
			}).catch((error) => {
				displayError(dataError);
				window.captureException(error);
			});

			axios.get('/api/v0/languages/supported').then(({
				data: data
			}) => {
				this.languages = data;
			}).catch((error) => {
				displayError(dataError);
				window.captureException(error);
			});

			axios.get('/api/v0/domains').then(({
				data: data
			}) => {
				this.domains = data;
			}).catch((error) => {
				displayError(dataError);
				window.captureException(error);
			});
		},

		/**
		 * Change the current page template when a new one is selected
		 */
		changeTemplate () {
			this.currentPage.page.id = this.pageTemplate.id;
			this.currentPage.page.title = this.pageTemplate.id ? this.pageTemplate.title : '';
			this.currentPage.page.html = this.pageTemplate.html;
			this.editor.resize();
			this.editor.setValue(this.currentPage.page.html, 1);
		},

		/**
		 * Get preview of page html to display to the screen
		 */
		async previewPage () {
			await axios.post('/api/v0/pages/preview', {
				html: this.editor.getValue()
			}).then(({
				data: data
			}) => {
				const myWindow = window.open('');

				myWindow.document.write(data);
				myWindow.document.title = this.$gettext('Preview');
			}).catch((error) => {
				if (error.response.status === 400) {
					displayError(error.response.data);
				} else {
					displayError(this.$gettext('Page preview failed'));
					window.captureException(error);
				}
			});
		},

		/**
		 * Update the available parent pages on update to language or domain.
		 *
		 * @returns {void}
		 */
		languageDomainUpdate () {
			if (this.currentPage.domainId && this.currentPage.languageId) {
				axios.get('/api/v0/domainpages', {
					params: {
						domainId: this.currentPage.domainId,
						languageId: this.currentPage.languageId
					}
				}).then(({
					data: data
				}) => {
					data.unshift(this.domainPages[0]);
					this.domainPages = data;
				});
			} else {
				this.domainPages = [this.domainPages[0], ];
			}
		},

		/**
		 * Update page
		 *
		 * @returns {void}
		 */
		submitPage () {
			const html = this.editor.getValue();

			if (!html) {
				displayError(this.$gettext('HTML is required'));
				return;
			}

			if (this.$refs.form.validate()) {
				axios.post('/api/v0/domainpages', {
					parentId: this.currentPage.parentId,
					pageId: this.currentPage.page.id,
					shortname: this.currentPage.shortname,
					title: this.currentPage.page.title,
					languageId: this.currentPage.languageId,
					domainId: this.currentPage.domainId,
					html: html,
					orderingPriorityStrength: this.currentPage.orderingPriorityStrength,
					published: this.currentPage.published,
					private: this.currentPage.private,
					downloadable: this.currentPage.downloadable
				}).then(({
					data: data
				}) => {
					inform(this.$gettext('Page submission successful'));
					debounce(() => {
						window.location.href = `/bo/pages/edit?id=${data}`;
					}, 1000)();
				}).catch((error) => {
					if (error.response.status === 400) {
						displayError(error.response.data);
					} else {
						displayError(this.$gettext('Submission failure'));
						window.captureException(error);
					}
				});
			}
		},

		/**
		 * Checks if our language is a flag path or just text.
		 *
		 * @param {string} language - language the email template is.
		 * @returns {boolean} Whether it is a flag path or not
		 */
		ifFile (language) {
			return !!/(?:\.([^.]+))?$/.exec(language)[1];
		}
	}
};
</script>
